/**
 * SimulatedBackend — finge o backend/websocket enquanto não existe o real.
 *
 * Mantém uma META de população (vinda do slider), faz jogadores ENTRAREM pelas
 * vias, e dirige o ciclo de cada um por temporizadores aleatórios:
 *   entrar → (vaga um tempo) → ir a um edifício → estacionar → ações → concluir
 *   → volta a vagar → ... e eventualmente SAIR (para respeitar a meta).
 *
 * Para plugar o backend REAL: troque esta classe por uma que receba mensagens
 * do websocket e chame os MESMOS métodos do CityAgents
 * (spawn/goto/actions/complete/leave). O resto do jogo não muda.
 */
const NAME_POOL = [
  'Lucas', 'Marina', 'Diego', 'Bianca', 'Rafael', 'Camila', 'Thiago', 'Aline',
  'Bruno', 'Júlia', 'Pedro', 'Sofia', 'Gabriel', 'Laura', 'Mateus', 'Beatriz',
  'Felipe', 'Helena', 'André', 'Carla', 'Vitor', 'Larissa', 'Gustavo', 'Manuela',
  'Rodrigo', 'Isabela', 'Fernando', 'Letícia', 'Daniel', 'Bruna', 'Eduardo',
  'Amanda', 'Caio', 'Natália', 'Henrique', 'Renata', 'Igor', 'Patrícia', 'Murilo',
  'Clara', 'Otávio', 'Yasmin', 'Ricardo', 'Débora', 'Vinícius', 'Tatiane'
];
import { randomAppearance } from '../entities/personModel.js';
import { randomCarAppearance } from '../entities/carModel.js';
import { PLAZA_ID } from '../world/cityLayout.js';

const COLORS_CAR = ['#eef2f0', '#b8c2bf', '#2fce5e', '#e8ecea', '#384b41', '#4a90d9'];
const COLORS_PED = ['#3ddc84', '#e6584d', '#f2c14e', '#5b8def', '#c879e6', '#39c6c6', '#ef8c3b'];

const rand = (lo, hi) => lo + Math.random() * (hi - lo);

export class SimulatedBackend {
  constructor(agents) {
    this.agents = agents;
    this.target = 0;
    this.jobs = new Map(); // id -> { kind, dueAt }
    this._seq = 0;
    this._spawnCooldown = 0;
    agents.onEvent((type, a) => this._onAgentEvent(type, a));
  }

  setTarget(n) {
    this.target = Math.max(0, Math.round(n));
  }

  /** Chamado a cada tick pelo PlayerService (now em ms). */
  tick(now) {
    // 1) manter a população na meta (entradas escalonadas / saídas)
    const active = this.agents.activeCount();
    if (active < this.target && now >= this._spawnCooldown) {
      // só avança o cooldown quando a entrada de fato acontece (senão a "boca"
      // estava ocupada → tenta de novo logo); evita backlog fora do mapa.
      const ok = this.agents.spawn(this._makeSpec());
      this._spawnCooldown = now + (ok ? 280 : 140);
    } else if (active > this.target) {
      const id = this.agents.pickLeaveCandidate();
      if (id) this.agents.leave(id);
    }

    // 2) disparar os comandos agendados
    for (const [id, job] of this.jobs) {
      if (now < job.dueAt) continue;
      this.jobs.delete(id);
      if (job.kind === 'next') this.agents.gotoNext(id);       // vai ao próximo edifício
      else if (job.kind === 'plaza') this.agents.gotoPlaza(id); // vai passear na praça
    }
  }

  _onAgentEvent(type, a) {
    if (type === 'parked') {
      // estacionou (virou pessoa): depois de um tempo, vai a OUTRO edifício — ou, ~1 em
      // cada 5, passear na PRAÇA. Dwell longo = mais avatares fora da via → ruas livres.
      this.agents.actions(a.id, this._demoAction()); // demonstra postura/outcome
      // cidade sem praça (Mexican Town): todo mundo vai sempre p/ o próximo edifício
      const goPlaza = PLAZA_ID != null && Math.random() < 0.2;
      this._schedule(a.id, goPlaza ? 'plaza' : 'next', rand(10, 22));
    } else if (type === 'plaza') {
      // chegou à praça: passeia um tempo pelos caminhos e depois segue p/ um prédio
      this._schedule(a.id, 'next', rand(14, 26));
    } else if (type === 'exited') {
      this.jobs.delete(a.id);
    }
  }

  _schedule(id, kind, secs) {
    this.jobs.set(id, { kind, dueAt: performance.now() + secs * 1000 });
  }

  /** Ação de demonstração: sorteia uma postura e, às vezes, um resultado — só
   *  para o modo simulado exibir as posturas e o emoji de outcome sem servidor. */
  _demoAction() {
    const kinds = ['wait', 'work', 'browse', 'talk'];
    const kind = kinds[Math.floor(Math.random() * kinds.length)];
    const r = Math.random();
    const outcome = r < 0.22 ? 'success' : r < 0.30 ? 'pending' : r < 0.36 ? 'fail' : undefined;
    return { kind, intent: 'demo', outcome };
  }

  _makeSpec() {
    // TODO jogador entra SEMPRE de carro; tem também o avatar de pessoa (estaciona)
    const carColor = COLORS_CAR[Math.floor(Math.random() * COLORS_CAR.length)];
    const pedColor = COLORS_PED[Math.floor(Math.random() * COLORS_PED.length)];
    return {
      id: `wabs-${++this._seq}`,
      name: NAME_POOL[Math.floor(Math.random() * NAME_POOL.length)],
      type: 'car',
      color: carColor,
      // o backend real pode mandar as suas; aqui o simulador sorteia (estável)
      carAppearance: randomCarAppearance({ color: carColor }),
      personAppearance: randomAppearance({ color: pedColor })
    };
  }
}
